import { useCallback } from 'react';
import { toast } from '@cyphex/ui';
import type { ConversationId } from '@cyphex/types';
import { uploadFile } from '../lib/uploadFile';
import type { VoiceClipResult } from './useVoiceRecorder';
import type { SendInput } from './useSendMessage';

/**
 * Uploads a recorded clip straight to object storage, then sends the message
 * with the resulting `fileId` + waveform. The bytes never touch apps/api.
 */
export function useVoiceSend(
  conversationId: ConversationId | null,
  send: (input: SendInput) => void,
): (clip: VoiceClipResult) => void {
  return useCallback(
    (clip: VoiceClipResult) => {
      if (!conversationId) return;
      const file = new File([clip.blob], `voice-${Date.now()}.webm`, {
        type: clip.blob.type || 'audio/webm',
      });

      void (async () => {
        try {
          const { fileId } = await uploadFile(file, { kind: 'conversation', id: conversationId });
          send({
            text: '',
            voice: {
              fileId,
              durationMs: clip.durationMs,
              waveform: clip.waveform,
            },
          });
        } catch {
          toast.error('Could not send that voice message.');
        }
      })();
    },
    [conversationId, send],
  );
}
